import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { vendorsApi, unwrapList } from "../api/resources";
import { useToast } from "../context/ToastContext";
import { useAsyncAction } from "../hooks/useAsyncAction";
import { useBuilderQuery } from "../hooks/useBuilderQuery";
import { Button, Empty, Input, Loader, Modal, Pagination } from "../components/ui";
import { ConfirmDialog, RecordActions } from "../components/actions";
import { VendorForm } from "../components/InstallmentPlan";
import { KhataSummary } from "../components/KhataSummary";
import { emptyVendorForm, vendorToForm } from "../components/forms";
import { COPY } from "../utils/copy";
import { emptyKhata } from "../utils/finance";
import { useDebouncedValue } from "../hooks/useDebouncedValue";

export default function Vendors() {
  const { isBuilder, query, ready } = useBuilderQuery();
  const toast = useToast();
  const { busy, run } = useAsyncAction();
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const liveSearch = useDebouncedValue(search);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyVendorForm);
  const [pendingDelete, setPendingDelete] = useState(null);

  const load = async () => {
    if (!ready) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await vendorsApi.list({ ...query, search: liveSearch, page, limit: 12 });
      const data = unwrapList(res);
      setItems(data.items);
      setPagination(data.pagination);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, liveSearch, query.builderId]);

  useEffect(() => {
    setPage(1);
  }, [liveSearch]);

  const closeModal = () => {
    setOpen(false);
    setEditing(null);
    setForm(emptyVendorForm);
  };

  const save = async (event) => {
    event.preventDefault();
    const result = await run(async () => {
      if (editing) return vendorsApi.update(editing.id, form);
      return vendorsApi.create(form);
    }, editing ? "Vendor updated." : "Vendor added.");

    if (result) {
      closeModal();
      load();
    }
  };

  const remove = async () => {
    if (!pendingDelete) return;
    const result = await run(() => vendorsApi.remove(pendingDelete.id), "Vendor removed.");
    if (result) {
      setPendingDelete(null);
      load();
    }
  };

  return (
    <div className="stack">
      <header className="page-head">
        <div>
          <p className="eyebrow">Who you buy from</p>
          <h1>Vendors</h1>
        </div>
        {isBuilder ? (
          <Button
            icon="plus"
            onClick={() => {
              setEditing(null);
              setForm(emptyVendorForm);
              setOpen(true);
            }}
          >
            Add vendor
          </Button>
        ) : null}
      </header>

      <form className="filters" onSubmit={(event) => event.preventDefault()}>
        <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search vendor" />
      </form>

      {loading ? (
        <Loader />
      ) : items.length === 0 ? (
        <Empty title="No vendors yet" text="Add the shop or person you buy cement, sand or labour from." />
      ) : (
        <div className="sale-grid">
          {items.map((vendor) => {
            const khata = vendor.khata || emptyKhata();
            return (
              <article key={vendor.id} className="sale-card">
                <Link to={`/vendors/${vendor.id}`} className="sale-card-main">
                  <div className="sale-card-top">
                    <h3>{vendor.name}</h3>
                    {khata.payable > 0 ? <span className="pill pill-credit">Udhar due</span> : null}
                  </div>
                  <p>{vendor.phone || "No phone"}</p>
                  <KhataSummary khata={khata} variant="card" />
                </Link>
                <div className="row-actions">
                  <Link className="btn btn-ghost btn-sm" to={`/vendors/${vendor.id}/statement`}>
                    Statement
                  </Link>
                  {khata.payable > 0 ? (
                    <Link className="btn btn-sm" to={`/vendors/${vendor.id}/udhar`}>
                      Pay
                    </Link>
                  ) : null}
                <RecordActions
                  onEdit={() => {
                    setEditing(vendor);
                    setForm(vendorToForm(vendor));
                    setOpen(true);
                  }}
                  onDelete={() => setPendingDelete(vendor)}
                />
                </div>
              </article>
            );
          })}
        </div>
      )}

      <Pagination page={pagination.page} totalPages={pagination.totalPages} onPage={setPage} />

      <Modal
        open={open}
        title={editing ? "Edit vendor" : "New vendor"}
        onClose={closeModal}
        footer={
          <>
            <Button variant="ghost" onClick={closeModal}>
              Cancel
            </Button>
            <Button type="submit" form="vendor-form" disabled={busy}>
              Save
            </Button>
          </>
        }
      >
        <VendorForm form={form} onChange={setForm} onSubmit={save} />
      </Modal>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Delete vendor?"
        text={COPY.deleteVendor}
        busy={busy}
        onClose={() => setPendingDelete(null)}
        onConfirm={remove}
      />
    </div>
  );
}
